import React, { useState, useEffect } from 'react';
import { Star, Trash2, Download, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Asset } from '@/utils/collectionsGraphQL';
import { CollectionAssetTile } from './CollectionAssetTile';
import { AssetTable } from './AssetTable';
import { BulkDeleteDialog } from './BulkDeleteDialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface CollectionAssetGridProps {
  assets: Asset[];
  viewMode: 'grid' | 'table';
  onOpenLightbox: (assetId: string) => void;
  onRefresh: () => void;
}

export const CollectionAssetGrid: React.FC<CollectionAssetGridProps> = ({
  assets,
  viewMode,
  onOpenLightbox,
  onRefresh,
}) => {
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [bulkOperationProgress, setBulkOperationProgress] = useState({ current: 0, total: 0 });
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

  const { toast } = useToast();

  // Toggle single asset selection
  const toggleAssetSelection = (assetId: string) => {
    setSelectedAssetIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      // Auto-disable selection mode when empty
      if (newSet.size === 0) {
        setIsSelectionMode(false);
      } else if (!isSelectionMode) {
        setIsSelectionMode(true);
      }
      return newSet;
    });
  };

  // Select all assets
  const selectAllAssets = () => {
    const allIds = new Set(assets.map((a) => a.id));
    setSelectedAssetIds(allIds);
    setIsSelectionMode(true);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedAssetIds(new Set());
    setIsSelectionMode(false);
  };

  // Bulk delete operation
  const handleBulkDelete = async () => {
    const assetIds = Array.from(selectedAssetIds);
    setBulkOperationInProgress(true);
    setBulkOperationProgress({ current: 0, total: assetIds.length });

    let successCount = 0;
    let errorCount = 0;
    const errors: Array<{ assetId: string; error: string }> = [];

    // Process in parallel batches of 5 for better UX
    const batchSize = 5;
    for (let i = 0; i < assetIds.length; i += batchSize) {
      const batch = assetIds.slice(i, i + batchSize);

      await Promise.allSettled(
        batch.map(async (assetId) => {
          try {
            const { error } = await supabase.from('app_assets').delete().eq('id', assetId);

            if (error) throw error;
            successCount++;
          } catch (error) {
            errorCount++;
            errors.push({
              assetId,
              error: error instanceof Error ? error.message : 'Unknown error',
            });
          }
        })
      );

      setBulkOperationProgress({
        current: Math.min(i + batchSize, assetIds.length),
        total: assetIds.length,
      });
    }

    setBulkOperationInProgress(false);
    setIsBulkDeleteDialogOpen(false);
    clearSelection();

    // Refetch assets
    await onRefresh();

    // Show results toast
    if (successCount > 0) {
      toast({
        title: 'Assets Deleted',
        description: `Successfully deleted ${successCount} of ${assetIds.length} assets`,
      });
    }

    if (errorCount > 0) {
      toast({
        title: 'Some Deletions Failed',
        description: `${errorCount} assets could not be deleted. Check console for details.`,
        variant: 'destructive',
      });
      console.error('Bulk delete errors:', errors);
    }
  };

  // Bulk favorite operation
  const handleBulkFavorite = async (shouldFavorite: boolean) => {
    const assetIds = Array.from(selectedAssetIds);
    setBulkOperationInProgress(true);
    setBulkOperationProgress({ current: 0, total: assetIds.length });

    let successCount = 0;
    let errorCount = 0;

    const batchSize = 10; // Favorites can be done faster
    for (let i = 0; i < assetIds.length; i += batchSize) {
      const batch = assetIds.slice(i, i + batchSize);

      await Promise.allSettled(
        batch.map(async (assetId) => {
          try {
            const { error } = await supabase
              .from('app_assets')
              .update({ is_favorited: shouldFavorite })
              .eq('id', assetId);

            if (error) throw error;
            successCount++;
          } catch (error) {
            errorCount++;
          }
        })
      );

      setBulkOperationProgress({
        current: Math.min(i + batchSize, assetIds.length),
        total: assetIds.length,
      });
    }

    setBulkOperationInProgress(false);
    clearSelection();
    await onRefresh();

    toast({
      title: shouldFavorite ? 'Assets Favorited' : 'Assets Unfavorited',
      description: `Successfully updated ${successCount} of ${assetIds.length} assets`,
    });
  };

  // Bulk export operation
  const handleBulkExport = () => {
    const assetIds = Array.from(selectedAssetIds);
    const selectedAssets = assets.filter((a) => assetIds.includes(a.id));

    // Create JSON export
    const exportData = {
      exported_at: new Date().toISOString(),
      asset_count: selectedAssets.length,
      assets: selectedAssets.map((asset) => ({
        id: asset.id,
        title: asset.title,
        header: asset.header,
        subheader: asset.subheader,
        slug: asset.slug,
        version: asset.version,
        primary_cropped_url: asset.primary_cropped_url,
        secondary_cropped_url: asset.secondary_cropped_url,
        is_favorited: asset.is_favorited,
      })),
    };

    // Download as JSON file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assets_export_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export Complete',
      description: `Exported ${selectedAssets.length} assets`,
    });
  };

  // Clear selection on view mode change
  useEffect(() => {
    clearSelection();
  }, [viewMode]);

  return (
    <div className="space-y-4">
      {/* Selection controls */}
      {assets.length > 0 && (
        <div className="flex items-center gap-3">
          <button
            onClick={selectAllAssets}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Select all
          </button>
          {selectedAssetIds.size > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                Clear selection
              </button>
              <span className="text-xs text-gray-400">({selectedAssetIds.size} selected)</span>
            </>
          )}
        </div>
      )}

      {/* Grid or Table rendering */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assets.map((asset) => (
            <CollectionAssetTile
              key={asset.id}
              asset={asset}
              isSelected={selectedAssetIds.has(asset.id)}
              onSelect={() => toggleAssetSelection(asset.id)}
              onClick={() => onOpenLightbox(asset.id)}
            />
          ))}
        </div>
      ) : (
        <AssetTable assets={assets} />
      )}

      {/* Floating action bar for bulk operations */}
      {selectedAssetIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-gray-900 text-white px-6 py-4 rounded-full shadow-xl">
          <span className="text-sm font-medium">{selectedAssetIds.size} selected</span>
          <div className="w-px h-6 bg-gray-600" />

          {/* Bulk Actions */}
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-gray-800 gap-2"
            onClick={() => handleBulkFavorite(true)}
            disabled={bulkOperationInProgress}
          >
            <Star className="h-4 w-4" />
            Favorite
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-gray-800 gap-2"
            onClick={() => handleBulkFavorite(false)}
            disabled={bulkOperationInProgress}
          >
            <Star className="h-4 w-4 fill-current" />
            Unfavorite
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-gray-800 gap-2"
            onClick={handleBulkExport}
            disabled={bulkOperationInProgress}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>

          <div className="w-px h-6 bg-gray-600" />

          <Button
            size="sm"
            variant="ghost"
            className="text-red-400 hover:text-red-300 hover:bg-gray-800 gap-2"
            onClick={() => setIsBulkDeleteDialogOpen(true)}
            disabled={bulkOperationInProgress}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="text-gray-400 hover:text-white hover:bg-gray-800"
            onClick={clearSelection}
            disabled={bulkOperationInProgress}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Progress overlay during bulk operations */}
      {bulkOperationInProgress && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 bg-white border border-gray-200 rounded-lg shadow-xl px-6 py-4">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Processing {bulkOperationProgress.current} of {bulkOperationProgress.total}...
              </p>
              <div className="w-64 bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${(bulkOperationProgress.current / bulkOperationProgress.total) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <BulkDeleteDialog
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        assetCount={selectedAssetIds.size}
        onConfirm={handleBulkDelete}
      />
    </div>
  );
};
